import {
  ChangeEvent,
  ClipboardEvent,
  FocusEvent,
  KeyboardEvent,
  RefObject,
  createRef,
  useCallback,
  useRef,
  useState,
} from 'react'

export const toHalfWidthDigit = (value: string): string =>
  value.replace(/[０-９]/g, char =>
    String.fromCharCode(char.charCodeAt(0) - 0xfee0)
  )

export const extractInsertedText = (
  previous: string,
  current: string
): string => {
  let head = 0
  while (
    head < previous.length &&
    head < current.length &&
    previous.charAt(head) === current.charAt(head)
  ) {
    head += 1
  }
  let tail = 0
  while (
    tail < previous.length - head &&
    tail < current.length - head &&
    previous.charAt(previous.length - 1 - tail) ===
      current.charAt(current.length - 1 - tail)
  ) {
    tail += 1
  }
  return current.substring(head, current.length - tail)
}

export const extractDigits = (value: string): string =>
  value.replace(/[^0-9]/g, '')

export type ResetCodeInputResult = {
  digits: string[]
  focusIndex?: number
}

export const fillResetCodeDigits = (
  digits: string[],
  index: number,
  insertedDigits: string
): ResetCodeInputResult => ({
  digits: digits.map((digit, i) => {
    const position = i - index
    return position >= 0 && position < insertedDigits.length
      ? insertedDigits.charAt(position)
      : digit
  }),
  focusIndex: Math.min(index + insertedDigits.length, digits.length - 1),
})

export const applyResetCodeInput = (
  digits: string[],
  index: number,
  inputValue: string
): ResetCodeInputResult => {
  const current = toHalfWidthDigit(inputValue)
  if (current === digits[index]) {
    return { digits }
  }
  const inserted = extractInsertedText(digits[index], current)
  if (inserted === '') {
    return {
      digits: digits.map((digit, i) => (i === index ? '' : digit)),
      focusIndex: Math.max(index - 1, 0),
    }
  }
  const insertedDigits = extractDigits(inserted)
  if (insertedDigits === '') {
    return { digits }
  }
  return fillResetCodeDigits(digits, index, insertedDigits)
}

export const RESET_CODE_LENGTH = 6

type ResetCodeInput = {
  resetCode: string
  digits: string[]
  refs: RefObject<HTMLInputElement>[]
  displayValueOf: (index: number) => string
  onChange: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number
  ) => void
  onKeyDown: (event: KeyboardEvent<HTMLDivElement>, index: number) => void
  onFocus: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  onPaste: (event: ClipboardEvent<HTMLDivElement>, index: number) => void
  onCompositionEnd: (index: number) => void
}

export const useResetCodeInput = (): ResetCodeInput => {
  const [digits, setDigits] = useState<string[]>(() =>
    Array.from({ length: RESET_CODE_LENGTH }, () => '')
  )
  const [composingInput, setComposingInput] = useState<
    { index: number; value: string } | undefined
  >(undefined)
  const refs = useRef(
    Array.from({ length: RESET_CODE_LENGTH }, () =>
      createRef<HTMLInputElement>()
    )
  )

  const focusAt = useCallback((index: number, selectValue: boolean) => {
    const input = refs.current[index]?.current
    if (!input) {
      return
    }
    input.focus()
    if (selectValue) {
      input.select()
    }
  }, [])
  const applyDigits = useCallback(
    (result: ResetCodeInputResult) => {
      setDigits(result.digits)
      const { focusIndex } = result
      if (focusIndex !== undefined) {
        focusAt(focusIndex, result.digits[focusIndex] !== '')
      }
    },
    [focusAt]
  )
  const commit = useCallback(
    (index: number, result: ResetCodeInputResult) => {
      // stateが同じ値のままだとReactが再描画せず、IME変換中に表示していた
      // 未確定文字がDOMに残るため、controlled componentだがDOMの値も直接揃える
      const input = refs.current[index]?.current
      if (input) {
        input.value = result.digits[index]
      }
      applyDigits(result)
    },
    [applyDigits]
  )
  const onChange = useCallback(
    (
      event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      index: number
    ) => {
      const { nativeEvent, target } = event
      if (nativeEvent instanceof InputEvent && nativeEvent.isComposing) {
        const composingDigits = extractDigits(
          extractInsertedText(digits[index], toHalfWidthDigit(target.value))
        )
        if (composingDigits === '') {
          setComposingInput({ index, value: target.value })
          return
        }
        setComposingInput(undefined)
        // 未確定文字から数字が取れた時点で反映したいので、blurで変換を打ち切る
        target.blur()
        applyDigits(fillResetCodeDigits(digits, index, composingDigits))
        return
      }
      setComposingInput(undefined)
      commit(index, applyResetCodeInput(digits, index, target.value))
    },
    [applyDigits, commit, digits]
  )
  const onCompositionEnd = useCallback(
    (index: number) => {
      setComposingInput(undefined)
      const input = refs.current[index]?.current
      if (!input) {
        return
      }
      commit(index, applyResetCodeInput(digits, index, input.value))
    },
    [commit, digits]
  )
  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>, index: number) => {
      if (event.key === 'Backspace') {
        if (digits[index] === '') {
          event.preventDefault()
          focusAt(index - 1, digits[index - 1] !== '')
        }
        return
      }
      if (
        event.ctrlKey ||
        event.metaKey ||
        event.altKey ||
        event.key.length !== 1
      ) {
        return
      }
      if (!/^[0-9]$/.test(event.key)) {
        event.preventDefault()
        return
      }
      if (digits[index] === event.key) {
        // 同じ数字を上書き入力すると値が変わらずonChangeが発火しないため、キー入力の時点で次マスへ送る
        event.preventDefault()
        const nextIndex = Math.min(index + 1, RESET_CODE_LENGTH - 1)
        focusAt(nextIndex, digits[nextIndex] !== '')
      }
    },
    [digits, focusAt]
  )
  const onFocus = useCallback(
    (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      event.target.select()
    },
    []
  )
  const onPaste = useCallback(
    (event: ClipboardEvent<HTMLDivElement>, index: number) => {
      event.preventDefault()
      const pasted = extractDigits(
        toHalfWidthDigit(event.clipboardData.getData('text'))
      )
      if (pasted === '') {
        return
      }
      commit(index, fillResetCodeDigits(digits, index, pasted))
    },
    [commit, digits]
  )
  const displayValueOf = useCallback(
    (index: number): string =>
      composingInput?.index === index ? composingInput.value : digits[index],
    [composingInput, digits]
  )

  return {
    resetCode: digits.join(''),
    digits,
    refs: refs.current,
    displayValueOf,
    onChange,
    onKeyDown,
    onFocus,
    onPaste,
    onCompositionEnd,
  }
}
